import { db } from "@/main";
import { functions } from "@/main";
import { getClientRef, getClientsAdministrationRef } from "./ClientRepository";
import {
  ArticleClass,
  BillingCatalog,
  BillingItem,
} from "../models/BillingCatalog";
import { isUsableFirestoreDocumentId } from "@/utils/BillingCatalogResolver";

const BILLING_CATALOGS_COLLECTION = "billingCatalogs";

/**
 * Returns the Firestore collection reference for billing catalogs.
 *
 * Dual-path resolution (follows `getSystemConfigurationRef()` pattern):
 * - Group clients (master or member): `clientsAdministration/{adminClientId}/billingCatalogs`
 * - Standalone clients: `clients/{clientId}/billingCatalogs`
 */
function getBillingCatalogsRef() {
  const adminRef = getClientsAdministrationRef();
  const baseRef = adminRef !== "" ? adminRef : getClientRef();
  return db.doc(baseRef).collection(BILLING_CATALOGS_COLLECTION);
}

/**
 * Fetches all active billing catalogs visible to the current client.
 *
 * Uses dual-path resolution — both master and member clients resolve to the same
 * `clientsAdministration` subcollection automatically. Standalone clients fall
 * back to `clients/{clientId}/billingCatalogs`.
 *
 * @returns Array of active billing catalogs.
 */
export async function fetchBillingCatalogs(): Promise<BillingCatalog[]> {
  const snapshot = await getBillingCatalogsRef()
    .where("status", "==", "active")
    .get();

  return snapshot.docs.map((doc) => ({
    ...doc.data(),
    id: doc.id,
  })) as BillingCatalog[];
}

/**
 * Fetches a single billing catalog by ID.
 *
 * @param catalogId - The document ID of the billing catalog.
 * @returns The billing catalog, or null if not found.
 */
export async function fetchBillingCatalog(
  catalogId: string
): Promise<BillingCatalog | null> {
  const doc = await getBillingCatalogsRef().doc(catalogId).get();
  if (!doc.exists) return null;
  return { ...doc.data(), id: doc.id } as BillingCatalog;
}

/**
 * Fetches all billing items for a specific catalog.
 *
 * Items are stored in the `billingItems` subcollection under each catalog document.
 *
 * @param catalogId - The document ID of the billing catalog.
 * @returns Array of billing items.
 */
export async function fetchBillingItems(
  catalogId: string
): Promise<BillingItem[]> {
  const snapshot = await getBillingCatalogsRef()
    .doc(catalogId)
    .collection("billingItems")
    .get();

  return snapshot.docs.map((doc) => ({
    ...doc.data(),
    id: doc.id,
  })) as BillingItem[];
}

/**
 * Fetches a single billing item by its article number.
 *
 * The article number is the document id, so this is one read rather than a scan of the
 * catalog. That is what makes a PDE's `(LV, billingItemId)` pair a direct reference: the
 * two values compose into a document path and no matching logic is involved.
 *
 * @param catalogId - The document ID of the billing catalog.
 * @param articleId - The article number exactly as the PDE carries it, suffix included.
 * @returns The billing item, or null when the catalog holds no such position.
 */
export async function fetchBillingItem(
  catalogId: string,
  articleId: string
): Promise<BillingItem | null> {
  if (!isUsableFirestoreDocumentId(articleId)) return null;

  const doc = await getBillingCatalogsRef()
    .doc(catalogId)
    .collection("billingItems")
    .doc(articleId)
    .get();

  if (!doc.exists) return null;
  return { ...doc.data(), id: doc.id } as BillingItem;
}

/**
 * Fetches all article classes for a specific catalog.
 *
 * Article classes define the grouping/sections for billing items.
 *
 * Sorted by `classId` ascending, in memory. X360 supplies no sort order, and the
 * class ids are numbered by design, so ascending id is the LV's own section order.
 * Deliberately not a Firestore `orderBy`: that would silently exclude any document
 * missing the field, which would render a whole catalog as one unclassified block.
 *
 * @param catalogId - The document ID of the billing catalog.
 * @returns Array of article classes, sorted by classId.
 */
export async function fetchArticleClasses(
  catalogId: string
): Promise<ArticleClass[]> {
  const snapshot = await getBillingCatalogsRef()
    .doc(catalogId)
    .collection("articleClasses")
    .get();

  return snapshot.docs
    .map((doc) => doc.data() as ArticleClass)
    .sort((a, b) => (a.classId ?? "").localeCompare(b.classId ?? ""));
}

/**
 * Updates specific fields on a billing item document.
 *
 * @param catalogId - The parent billing catalog document ID.
 * @param itemId - The billing item document ID.
 * @param fields - Partial fields to update (typically `limit` and/or `qualifier`).
 */
export async function updateBillingItem(
  catalogId: string,
  itemId: string,
  fields: Partial<Pick<BillingItem, "limit" | "qualifier">>
): Promise<void> {
  await getBillingCatalogsRef()
    .doc(catalogId)
    .collection("billingItems")
    .doc(itemId)
    .update(fields);
}

/**
 * Updates catalog-level fields on a billing catalog document.
 *
 * @param catalogId - The billing catalog document ID.
 * @param fields - Partial fields to update (e.g., `validFrom`, `validUntil`).
 */
export async function updateBillingCatalog(
  catalogId: string,
  fields: Partial<Pick<BillingCatalog, "validFrom" | "validUntil">>
): Promise<void> {
  await getBillingCatalogsRef().doc(catalogId).update(fields);
}

/** Result returned by the importBillingCatalogsCallable Cloud Function. */
export interface ImportBillingCatalogsResult {
  success: boolean;
  catalogs: Array<{
    catalogId: string;
    catalogName: string;
    itemsImported: number;
    itemsWithPreservedEdits: number;
    newItems: number;
    removedItems: number;
  }>;
  totalItems: number;
  importedAt: string;
}

/**
 * Triggers a manual billing catalog import from the Haufe X360 API.
 *
 * Calls the `importBillingCatalogsCallable` Cloud Function which fetches
 * billing items from the external API, groups them by catalog owner/PriceClass,
 * and writes them to Firestore. Existing `limit`/`qualifier` edits are preserved.
 *
 * No input parameters required — the function resolves the caller's group automatically.
 *
 * @returns Import result with per-catalog statistics.
 */
export async function importBillingCatalogs(): Promise<ImportBillingCatalogsResult> {
  const callable = functions.httpsCallable("importBillingCatalogsCallable");
  const result = await callable({});
  return result.data as ImportBillingCatalogsResult;
}
